import { useCallback, useEffect, useState } from "react";
import { Table, message } from "antd";
import DataKitApi from "../../../../api/DataKitApi";
import { MenuSummaryDto } from "../../../../domain/DataKitDto";
import { dataKitThumbColors } from "./DataKitThumb";
import { DataKitMenuView } from "./DataKitMenuView";
import { useMenuLocation } from "./menuLocation";
import "./DataKitAccountMenusTab.scss";

interface Props {
  onCountChange: (count: number) => void;
}

/**
 * Item Libraries › Menus (Oct 12: menus come from designs). Every design that uses items from the
 * account's libraries is listed here as a menu, named after the design - nothing to create, name or
 * attach. A row opens the menu's read-only view: its libraries and its menu prices. Menu prices are
 * set per design, from Timber or the item drawer.
 */
export function DataKitAccountMenusTab({ onCountChange }: Props) {
  const [menus, setMenus] = useState<MenuSummaryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewing, setViewing] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    DataKitApi.listAccountMenus()
      .then((res) => {
        const next = res.data || [];
        setMenus(next);
        onCountChange(next.length);
      })
      .catch(() => message.error("Could not load menus"))
      .finally(() => setLoading(false));
  }, [onCountChange]);

  useEffect(() => {
    load();
  }, [load]);

  const locationOf = useMenuLocation();
  const rows = [...menus].sort(
    (a, b) => (locationOf(a) || "").localeCompare(locationOf(b) || "") || a.name.localeCompare(b.name)
  );

  const columns = [
    {
      title: "Menu",
      key: "menu",
      width: "26%",
      render: (_: unknown, menu: MenuSummaryDto) => <span className="menus-name">{menu.name}</span>,
      sorter: (a: MenuSummaryDto, b: MenuSummaryDto) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
      showSorterTooltip: false,
    },
    {
      title: "Location",
      key: "location",
      render: (_: unknown, menu: MenuSummaryDto) =>
        locationOf(menu) ? (
          <span className="menus-location">{locationOf(menu)}</span>
        ) : (
          <span className="menus-empty">--</span>
        ),
    },
    {
      title: "Uses items from",
      key: "libraries",
      render: (_: unknown, menu: MenuSummaryDto) => (
        <span className="menus-libraries">
          {menu.libraries.map((lib) => {
            const { bg, fg } = dataKitThumbColors(lib.name || "Untitled");
            return (
              <span key={lib.id} className="menus-library-chip" style={{ background: bg, color: fg }}>
                {lib.name || "Untitled"}
              </span>
            );
          })}
        </span>
      ),
    },
    {
      title: "Items",
      dataIndex: "itemCount",
      key: "itemCount",
      width: 90,
      align: "right" as const,
    },
    {
      title: "Menu prices",
      key: "menuPriceCount",
      width: 120,
      align: "right" as const,
      render: (_: unknown, menu: MenuSummaryDto) =>
        menu.menuPriceCount > 0 ? menu.menuPriceCount : <span className="menus-empty">--</span>,
    },
  ];

  return (
    <div className="data-kit-account-menus-tab">
      <p className="menus-caption">
        Every design that uses items from your item libraries is listed here as a menu. To rename a
        menu, rename its design.
      </p>

      <Table
        className="menus-table"
        dataSource={rows}
        columns={columns}
        rowKey="designId"
        loading={loading}
        pagination={false}
        rowClassName="menus-row"
        onRow={(menu) => ({ onClick: () => setViewing(menu.designId) })}
        locale={{ emptyText: "No design uses library items yet." }}
      />

      <p className="menus-footnote">Designs that don&apos;t use any library items aren&apos;t listed.</p>

      {viewing && <DataKitMenuView designId={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}
